import { useQuery } from "@tanstack/react-query";
import { getUrl } from "aws-amplify/storage";

/**
 * Custom hook to fetch and cache AWS S3 image URLs
 * @param imagePath - The S3 path to the image (displayImg)
 * @param options - Additional query options
 * @returns Query result with the image URL
 */
export const useImageUrl = (
  imagePath: string | null | undefined,
  options?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
  }
) => {
  return useQuery({
    queryKey: ["imageUrl", imagePath],
    queryFn: async () => {
      if (!imagePath) {
        return null;
      }
      try {
        const result = await getUrl({
          path: imagePath,
        });
        return result.url.toString();
      } catch (error) {
        console.error("Failed to get image URL:", error);
        throw error;
      }
    },
    enabled: !!imagePath && (options?.enabled !== false),
    staleTime: options?.staleTime ?? 1000 * 60 * 15, // 15 minutes default
    gcTime: options?.gcTime ?? 1000 * 60 * 60, // 1 hour default
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook for multiple image URLs - useful for lists of images
 * @param imagePaths - Array of S3 paths
 * @returns Object with URLs keyed by the original path
 */
export const useImageUrls = (imagePaths: (string | null | undefined)[]) => {
  const validPaths = imagePaths.filter(Boolean) as string[];
  
  const queries = validPaths.map(path => ({
    queryKey: ["imageUrl", path],
    queryFn: async () => {
      try {
        const result = await getUrl({ path });
        return { path, url: result.url.toString() };
      } catch (error) {
        console.error(`Failed to get image URL for ${path}:`, error);
        throw error;
      }
    },
  }));

  return { queries };
};
